export type Testimonial = {
  quote: string
  name: string
  location: string
  initials?: string
}

export const heroPills = [
  '1 Main Volume',
  '5 Bonuses',
  '50+ Sessions',
  '60+ Drills',
  'U8 to U18',
]

export const stats = [
  { value: '500+', label: 'Coaches' },
  { value: '1+5', label: 'Volume & Bonuses' },
  { value: '4.9/5', label: 'Rating ⭐' },
  { value: '🌍', label: 'Worldwide' },
]

export const topTestimonials: Testimonial[] = [
  {
    quote:
      'I planned my entire season in one evening. Every session now has a clear objective and I walk to the ground with full confidence.',
    name: 'James M.',
    location: 'Lancashire, UK',
  },
  {
    quote:
      'My young players are developing so much faster. The progressions are logical and the kids can feel themselves improving.',
    name: 'Coach David',
    location: 'Melbourne, Australia',
  },
  {
    quote:
      'I no longer spend hours searching for drills online. I open Cricket Ready and everything I need is right there.',
    name: 'Priya T.',
    location: 'Auckland, NZ',
  },
]

export const painPoints = [
  'You arrive at training without a real plan',
  'You spend hours preparing sessions before every training',
  'You keep searching for new drills online',
  'Skill levels in your squad vary wildly',
  "You don't know how to structure a full season progression",
  'Sessions always feel improvised',
]

export const overviewStats = [
  { value: '200+', label: 'Pages' },
  { value: '50+', label: 'Sessions' },
  { value: '60+', label: 'Drills' },
  { value: 'All', label: 'Age Groups' },
]

export const coverageTags = [
  'U8',
  'U10',
  'U12',
  'U14',
  'U16',
  'U18',
  'Batting',
  'Bowling',
  'Fielding',
  'Tactics',
  'Match Prep',
  'Assessment',
]

export type Module = {
  number: string
  title: string
  description: string
  tags: string[]
  color: string
  dot: string
}

export const modules: Module[] = [
  {
    number: '01',
    title: 'Annual Planning & Season Structure',
    description:
      'Full season framework with weekly planning templates so every block of the year has a purpose.',
    tags: ['Pre-season', 'In-season', 'Off-season', 'Weekly planner sheets'],
    color: '#22c55e',
    dot: '🟢',
  },
  {
    number: '02',
    title: '50 Complete Training Sessions',
    description:
      'Ready-to-use sessions for U8 through U18 — warm-up, main block, game, and coaching cues included.',
    tags: ['U8', 'U10', 'U12', 'U14', 'U16', 'U18'],
    color: '#eab308',
    dot: '🟡',
  },
  {
    number: '03',
    title: 'Batting Progression System',
    description:
      'Structured batting development from fundamentals to advanced technique, stage by stage.',
    tags: ['Technique', 'Footwork', 'Shot Progressions', 'Drills'],
    color: '#3b82f6',
    dot: '🔵',
  },
  {
    number: '04',
    title: 'Bowling Progression System',
    description:
      'Pace & spin bowling frameworks with action checkpoints you can coach from the side of the net.',
    tags: ['Seam', 'Swing', 'Off-spin', 'Leg-spin', 'Yorkers'],
    color: '#ef4444',
    dot: '🔴',
  },
  {
    number: '05',
    title: 'Fielding & Wicketkeeping',
    description:
      'Ground fielding, catching and throwing mechanics with keeper-specific systems.',
    tags: ['Ground Fielding', 'Catching', 'Throwing', 'WK Systems'],
    color: '#a855f7',
    dot: '🟣',
  },
  {
    number: '06',
    title: 'Player Assessment & Coaching Tools',
    description:
      'Assessment forms, tracking templates and coaching logs to prove progress across the season.',
    tags: ['Assessment', 'Tactics', 'Match Prep', '24-Week Log'],
    color: '#f97316',
    dot: '🟠',
  },
]

export const moduleIncludes = [
  'Annual Planning Templates',
  'Complete Ready-to-Use Sessions',
  'Skill Progressions by Age',
  'Coaching Cue Libraries',
  'Player Tracking Forms',
  'Warm-Up & Drill Banks',
  'Match Preparation Protocols',
  'Mental Skills Framework',
]

export type Bonus = {
  number: string
  title: string
  description: string
  price: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'Full Season Weekly Planner',
    description:
      '54-page visual season planner that maps every week of your year on one sheet.',
    price: '$59',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/59495913-6ea9-4fec-b9d5-ecb9e8a5864e.jpg',
  },
  {
    number: '02',
    title: 'Player Development Tracker',
    description:
      '27-page individual player tracking system with skill scores and progress notes.',
    price: '$54',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/620fc273-19e4-449b-ba89-96ea6767ec9a.jpg',
  },
  {
    number: '03',
    title: '50 Drill Cards — Print & Laminate',
    description:
      '50 professional drill cards you can print, laminate and take straight to the ground.',
    price: '$64',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9d044db2-7633-4d98-9459-4c552d7d2c22.jpg',
  },
  {
    number: '04',
    title: 'Coaching Communication Pack',
    description:
      '12 professional templates for parents, players, clubs and selection conversations.',
    price: '$49',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f7ff19c3-53f7-48b8-bc69-e0b6a012d61f.jpg',
  },
  {
    number: '05',
    title: 'Pre-Season Fitness Testing Pack',
    description:
      '13-page complete fitness assessment with benchmarks by age group.',
    price: '$48',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/169073e6-935a-473d-9d74-f0e8efe10879.jpg',
  },
]

export const pricingChecklist = [
  '1 Complete Volume',
  '5 Strategic Bonuses',
  '50+ Training Sessions',
  '60+ Professional Drills',
  'Lifetime Access',
]

export const coachTestimonials: Testimonial[] = [
  {
    initials: 'JH',
    name: 'James H.',
    location: 'Yorkshire, UK',
    quote:
      'I mapped out my whole season in one night. Every single practice now has a clear goal.',
  },
  {
    initials: 'MK',
    name: 'Mark K.',
    location: 'Sydney, Australia',
    quote:
      'My team improved more in two months than in the entire previous season.',
  },
  {
    initials: 'SP',
    name: 'Sunita P.',
    location: 'Cape Town, SA',
    quote:
      'I used to spend hours on YouTube hunting for ideas. Now I open Cricket Ready and everything is right there.',
  },
  {
    initials: 'RT',
    name: 'Richard T.',
    location: 'Christchurch, NZ',
    quote:
      'The player assessment forms changed everything. My conversations with parents are now professional and data-driven.',
  },
  {
    initials: 'EW',
    name: 'Emma W.',
    location: 'Manchester, UK',
    quote:
      'As a first-year coach I was completely overwhelmed. Cricket Ready gave me a full system from day one.',
  },
  {
    initials: 'AB',
    name: 'Arun B.',
    location: 'Mumbai, India',
    quote:
      'The bowling progression framework is outstanding. My pacers have better run-ups.',
  },
]

export const faqs = [
  {
    question: 'How does Cricket Ready work?',
    answer:
      'You get instant digital access to the main volume and all five bonuses. Open the season plan, pick the session that matches your week and age group, print it or read it on your phone at the ground. No preparation required.',
  },
  {
    question: 'Does it work for any age group?',
    answer:
      'Yes. Every session and progression is built for U8 through U18, with age-appropriate loads, coaching cues and constraints clearly marked on each page.',
  },
  {
    question: 'Can I edit the material?',
    answer:
      'The planners, trackers and communication templates are provided in editable and printable formats, so you can add your club branding, player names and your own notes.',
  },
  {
    question: 'Are the bonuses included in the price?',
    answer:
      'All five bonuses — worth $274 — are included free with the complete package at no extra cost. There are no upsells after checkout.',
  },
  {
    question: 'Is it suitable for beginner coaches?',
    answer:
      'It is built for exactly that situation. Each session tells you what to say, what to look for and how to progress or regress the drill, so you can coach with confidence in your first season.',
  },
  {
    question: 'Is it useful for school PE teachers too?',
    answer:
      'Yes. The sessions work with large groups, limited equipment and short time blocks, which makes them a natural fit for school programmes and after-school clubs.',
  },
  {
    question: 'How long does access last?',
    answer:
      'Lifetime access. Download everything once and keep it for every season, including any future updates to the volume.',
  },
  {
    question: 'What makes this different from other coaching resources?',
    answer:
      'Most resources give you isolated drills. Cricket Ready gives you a complete season system — planning, progressions, sessions, assessment and communication — all connected and ready to run.',
  },
]

export const comparisonRows = [
  {
    topic: 'Arriving at training',
    without: 'Improvising on the spot with no plan',
    with: '10 min/week. Everything ready.',
  },
  {
    topic: 'Finding drills',
    without: 'Hours lost searching online',
    with: '60+ drills + full sessions by level',
  },
  {
    topic: 'Skill progression',
    without: 'Random, impossible to measure',
    with: 'Logical, measurable progression',
  },
  {
    topic: 'Player assessment',
    without: 'Gut feeling and memory',
    with: 'Evaluation sheets and tracking',
  },
  {
    topic: 'Season structure',
    without: 'Week-to-week guesswork',
    with: 'Full 24-week planning system',
  },
  {
    topic: 'Parent communication',
    without: 'Awkward, written from scratch',
    with: '12 professional templates',
  },
]
